import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import api from '../../api';

const DATE_FORMAT = 'YYYY-MM-DD';
const STATISTICS_TIMEOUT = 60000;
const EVENT_SELECTOR_STATUS = 'SELLING,NOT_SELLING,ARCHIVED';

export const fetchOrganizerTicketSalesStatistics = createAsyncThunk(
  'orderStatistics/fetchOrganizerTicketSalesStatistics',
  ({ from, to, eventId, organizerId, paymentPointId }) =>
    api
      .get('/statistics/organizer/ticketSales', {
        params: {
          organizer_id: organizerId,
          ...(paymentPointId ? { payment_point_id: paymentPointId } : {}),
          from,
          to,
          ...(eventId ? { event_id: eventId } : {}),
        },
        timeout: STATISTICS_TIMEOUT,
      })
      .then((response) => response.data)
);

export const fetchOrganizerTopPerformersStatistics = createAsyncThunk(
  'orderStatistics/fetchOrganizerTopPerformersStatistics',
  ({ organizerId, paymentPointId, from, to }) =>
    api
      .get('/statistics/organizer/topPerformers', {
        params: {
          organizer_id: organizerId,
          ...(paymentPointId ? { payment_point_id: paymentPointId } : {}),
          from,
          to,
        },
        timeout: STATISTICS_TIMEOUT,
      })
      .then((response) => response.data)
);

export const fetchOrderStatisticsEvents = createAsyncThunk(
  'orderStatistics/fetchEvents',
  ({ page = 1, limit = 100, organizerId }) =>
    api
      .get('/events/selector', {
        params: {
          page,
          limit,
          status: EVENT_SELECTOR_STATUS,
          ...(organizerId ? { organizer_id: organizerId } : {}),
        },
        timeout: STATISTICS_TIMEOUT,
      })
      .then((response) => ({ ...response.data, requestedPage: page }))
);

const initialState = {
  data: null,
  topPerformersData: null,
  events: [],
  eventsCurrentPage: 0,
  eventsHasMore: true,
  error: null,
  isLoading: false,
  isLoadingTopPerformers: false,
  isLoadingEvents: false,
  options: {
    from: dayjs().subtract(2, 'week').format(DATE_FORMAT),
    to: dayjs().format(DATE_FORMAT),
    eventId: '',
    organizerId: '',
  },
};

const statisticsSlice = createSlice({
  name: 'orderStatistics',
  initialState,
  reducers: {
    setOrderStatisticsDateRange: (state, action) => {
      const { from, to, eventId, organizerId } = action.payload;
      if (from !== undefined) state.options.from = from;
      if (to !== undefined) state.options.to = to;
      if (eventId !== undefined) state.options.eventId = eventId;
      if (organizerId !== undefined) state.options.organizerId = organizerId;
    },
    clearOrderStatisticsEvents: (state) => {
      state.events = [];
      state.eventsCurrentPage = 0;
      state.eventsHasMore = true;
      state.isLoadingEvents = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrderStatisticsEvents.pending, (state) => {
      state.isLoadingEvents = true;
      state.error = null;
    });

    builder.addCase(fetchOrderStatisticsEvents.fulfilled, (state, action) => {
      state.isLoadingEvents = false;
      state.events =
        action.payload.requestedPage === 1
          ? action.payload.events || []
          : state.events.concat(action.payload.events || []);
      state.eventsCurrentPage = action.payload.currentPage || action.payload.requestedPage;
      state.eventsHasMore = Boolean(action.payload.hasMore);
    });

    builder.addCase(fetchOrderStatisticsEvents.rejected, (state, action) => {
      state.isLoadingEvents = false;
      state.error = action.error.message;
    });

    builder.addCase(fetchOrganizerTicketSalesStatistics.pending, (state) => {
      state.isLoading = true;
      state.data = null;
      state.error = null;
    });

    builder.addCase(fetchOrganizerTicketSalesStatistics.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data = action.payload;
    });

    builder.addCase(fetchOrganizerTicketSalesStatistics.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(fetchOrganizerTopPerformersStatistics.pending, (state) => {
      state.isLoadingTopPerformers = true;
      state.topPerformersData = null;
      state.error = null;
    });

    builder.addCase(fetchOrganizerTopPerformersStatistics.fulfilled, (state, action) => {
      state.isLoadingTopPerformers = false;
      state.topPerformersData = action.payload;
    });

    builder.addCase(fetchOrganizerTopPerformersStatistics.rejected, (state, action) => {
      state.isLoadingTopPerformers = false;
      state.error = action.error.message;
    });
  },
});

export const { clearOrderStatisticsEvents, setOrderStatisticsDateRange } = statisticsSlice.actions;

export default statisticsSlice.reducer;
